import { router } from 'expo-router'
import buildOrgPath from '@/utils/orgRoute'
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useWindowDimensions, View } from 'react-native'
import Animated, {
  Easing,
  runOnJS,
  useAnimatedProps,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated'
import { Path, Svg } from 'react-native-svg'
import { StyleSheet } from 'react-native-unistyles'

import TourTooltip from '@/components/tour/TourTooltip'
import type {
  SpotEntry,
  SpotMeasurement,
  TourConfig,
  TourStep,
} from '@/components/tour/types'

// ---------------------------------------------------------------------------
// Animated SVG path component
// ---------------------------------------------------------------------------

const AnimatedPath = Animated.createAnimatedComponent(Path)

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MOVE_DURATION = 350
const MOVE_EASING = Easing.bezier(0.4, 0, 0.2, 1) // Material ease-in-out
const MOVE_CONFIG = { duration: MOVE_DURATION, easing: MOVE_EASING }
const OVERLAY_COLOR = 'rgba(0,0,0,0.75)'
const TOOLTIP_GAP = 16
const TOOLTIP_MAX_WIDTH = 340
const TOOLTIP_H_MARGIN = 16
const TOOLTIP_V_MARGIN = 32 // minimum space from screen top/bottom
const SPOT_WAIT_TIMEOUT = 5000
const SPOT_MEASURE_RETRY_DELAY = 50
const SPOT_MEASURE_MAX_RETRIES = 30
const SCROLL_SETTLE_DELAY = 350
/** Delay after cross-screen navigation to let the transition animation settle */
const NAV_SETTLE_DELAY = 450

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

interface TourContextValue {
  registerSpot: (id: string, entry: SpotEntry) => void
  unregisterSpot: (id: string) => void
  start: (config: TourConfig) => Promise<void>
  stop: () => void
  isActive: boolean
}

const TourContext = createContext<TourContextValue | null>(null)

export const useTour = () => {
  const ctx = useContext(TourContext)
  if (!ctx) throw new Error('useTour must be used within a <TourProvider>')
  return ctx
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const delay = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms))

/**
 * Build an SVG path string consisting of a full-screen rectangle with a
 * rounded-rectangle cutout removed via the even-odd fill rule.
 */
const buildOverlayPath = (
  screenW: number,
  screenH: number,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
): string => {
  'worklet'
  // Clamp radius so it doesn't exceed half the cutout dimension
  const cr = Math.max(0, Math.min(r, w / 2, h / 2))

  // Outer rect (full screen, clockwise)
  // Inner rounded rect (cutout, clockwise — evenodd makes it transparent)
  return (
    `M0,0 H${screenW} V${screenH} H0 Z ` +
    `M${x + cr},${y} ` +
    `H${x + w - cr} Q${x + w},${y} ${x + w},${y + cr} ` +
    `V${y + h - cr} Q${x + w},${y + h} ${x + w - cr},${y + h} ` +
    `H${x + cr} Q${x},${y + h} ${x},${y + h - cr} ` +
    `V${y + cr} Q${x},${y} ${x + cr},${y} Z`
  )
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export const TourProvider = ({ children }: { children: React.ReactNode }) => {
  const { width: screenWidth, height: screenHeight } = useWindowDimensions()

  // ----- Spot registry -----------------------------------------------------
  const spotsRef = useRef(new Map<string, SpotEntry>())
  const pendingResolversRef = useRef(new Map<string, () => void>())

  // ----- Tour state --------------------------------------------------------
  const [isActive, setIsActive] = useState(false)
  const [currentStep, setCurrentStep] = useState<TourStep | null>(null)
  const [stepIndex, setStepIndex] = useState(0)
  const [totalSteps, setTotalSteps] = useState(0)

  const tourConfigRef = useRef<TourConfig | null>(null)
  const stepIndexRef = useRef(0)
  const isAnimatingRef = useRef(false)
  /** Current cutout rect — kept in sync so onLayout can reposition the tooltip */
  const cutoutRectRef = useRef({ x: 0, y: 0, w: 0, h: 0 })
  /** Overlay origin offset — needed on Android where measureInWindow returns
   *  absolute screen coords but the overlay may start below the status bar */
  const overlayRef = useRef<View>(null)
  const overlayOffsetRef = useRef({ x: 0, y: 0 })

  // ----- Animated values ---------------------------------------------------
  const cutoutX = useSharedValue(0)
  const cutoutY = useSharedValue(0)
  const cutoutW = useSharedValue(0)
  const cutoutH = useSharedValue(0)
  const cutoutR = useSharedValue(8)

  /** Measured height of the tooltip card (updated via onLayout) */
  const tooltipHeight = useSharedValue(120)
  /** Tooltip position — set explicitly, NOT derived from cutout */
  const tooltipTop = useSharedValue(0)
  const tooltipLeft = useSharedValue(0)

  /** Main overlay opacity (fade in on start, fade out on stop) */
  const overlayOpacity = useSharedValue(0)
  /** Tooltip's own opacity (fades during step transitions) */
  const tooltipOpacity = useSharedValue(0)

  // ----- Registration ------------------------------------------------------

  const registerSpot = useCallback((id: string, entry: SpotEntry) => {
    spotsRef.current.set(id, entry)
    const resolver = pendingResolversRef.current.get(id)
    if (resolver) {
      pendingResolversRef.current.delete(id)
      resolver()
    }
  }, [])

  const unregisterSpot = useCallback((id: string) => {
    spotsRef.current.delete(id)
  }, [])

  // ----- Waiting & measuring -----------------------------------------------

  /** Returns a promise that resolves once the given spot ID is registered. */
  const waitForSpot = useCallback((spotId: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (spotsRef.current.has(spotId)) {
        resolve()
        return
      }
      const timer = setTimeout(() => {
        pendingResolversRef.current.delete(spotId)
        reject(new Error(`Tour spot "${spotId}" not found within timeout`))
      }, SPOT_WAIT_TIMEOUT)

      pendingResolversRef.current.set(spotId, () => {
        clearTimeout(timer)
        resolve()
      })
    })
  }, [])

  /** Scroll a spot into view if it has a registered scroll container. */
  const scrollSpotIntoView = useCallback(async (spotId: string) => {
    const entry = spotsRef.current.get(spotId)
    if (entry?.scrollIntoView) {
      await entry.scrollIntoView()
      await delay(SCROLL_SETTLE_DELAY)
    }
  }, [])

  /** Measure a spot, retrying until the view has non-zero dimensions. */
  const measureSpot = useCallback(
    async (spotId: string): Promise<SpotMeasurement | null> => {
      const entry = spotsRef.current.get(spotId)
      if (!entry) return null

      for (let i = 0; i < SPOT_MEASURE_MAX_RETRIES; i++) {
        const m = await entry.measure()
        if (m && m.width > 0 && m.height > 0) return m
        await delay(SPOT_MEASURE_RETRY_DELAY)
      }
      return null
    },
    []
  )

  // ----- Overlay offset measurement -----------------------------------------

  /** Measure the overlay's screen origin so we can convert measureInWindow
   *  coords (absolute screen) to overlay-relative coords. On iOS this is
   *  typically (0,0), but on Android the status bar pushes the overlay down. */
  const measureOverlayOffset = useCallback((): Promise<void> => {
    return new Promise((resolve) => {
      if (!overlayRef.current) {
        resolve()
        return
      }
      overlayRef.current.measureInWindow((x, y) => {
        overlayOffsetRef.current = { x, y }
        resolve()
      })
    })
  }, [])

  // ----- Tooltip positioning ------------------------------------------------

  /** Recompute tooltip top/left from the current cutout rect + tooltip height */
  const positionTooltip = useCallback(() => {
    const { x, y, w, h } = cutoutRectRef.current
    const th = tooltipHeight.value
    const spaceAbove = y - TOOLTIP_V_MARGIN
    const spaceBelow = screenHeight - (y + h) - TOOLTIP_V_MARGIN
    const preferAbove = y + h / 2 > screenHeight / 2

    let newTop: number
    if (preferAbove && spaceAbove >= th + TOOLTIP_GAP) {
      newTop = y - th - TOOLTIP_GAP
    } else if (!preferAbove && spaceBelow >= th + TOOLTIP_GAP) {
      newTop = y + h + TOOLTIP_GAP
    } else {
      newTop = (screenHeight - th) / 2
    }

    const maxW = Math.min(screenWidth - TOOLTIP_H_MARGIN * 2, TOOLTIP_MAX_WIDTH)
    const idealLeft = x + w / 2 - maxW / 2
    const newLeft = Math.max(
      TOOLTIP_H_MARGIN,
      Math.min(idealLeft, screenWidth - maxW - TOOLTIP_H_MARGIN)
    )

    tooltipTop.value = newTop
    tooltipLeft.value = newLeft
  }, [screenHeight, screenWidth, tooltipHeight, tooltipTop, tooltipLeft])

  // ----- Core animation logic ----------------------------------------------

  const animateToStep = useCallback(
    async (
      index: number,
      animate: boolean = true,
      /** 1 = forward, -1 = backward — used to skip missing spots */
      direction: 1 | -1 = 1
    ) => {
      const config = tourConfigRef.current
      if (!config || index < 0 || index >= config.steps.length) return

      const step = config.steps[index]!
      const prevIndex = stepIndexRef.current

      // Pre-transition callback
      if (animate && config.onBeforeStepChange) {
        await config.onBeforeStepChange(prevIndex, index)
      }

      // Navigate if the target step lives on a different screen
      let didNavigate = false
      if (step.navigateTo) {
        const prevStep = config.steps[prevIndex]
        if (!prevStep || prevStep.navigateTo !== step.navigateTo) {
          router.navigate(buildOrgPath(step.navigateTo) as never)
          didNavigate = true
        }
      }

      // For same-screen steps, the spot should already be registered.
      // If it's not, it's conditionally rendered and won't appear — skip it.
      if (!didNavigate && !spotsRef.current.has(step.spotId)) {
        console.warn(`Tour: spot "${step.spotId}" not registered, skipping`)
        const nextIdx = index + direction
        if (nextIdx >= 0 && nextIdx < config.steps.length) {
          return animateToStep(nextIdx, animate, direction)
        }
        return
      }

      // Wait for the spot to mount (handles cross-screen navigation)
      try {
        await waitForSpot(step.spotId)
      } catch {
        // Spot never appeared — skip to next in the same direction
        console.warn(`Tour: spot "${step.spotId}" not found, skipping`)
        const nextIdx = index + direction
        if (nextIdx >= 0 && nextIdx < config.steps.length) {
          return animateToStep(nextIdx, animate, direction)
        }
        return
      }

      // Let the navigation transition animation finish so measureInWindow
      // returns final (not mid-animation) coordinates
      if (didNavigate) {
        await delay(NAV_SETTLE_DELAY)
      }

      // Scroll the spot into view if it's inside a ScrollView, then measure
      await scrollSpotIntoView(step.spotId)
      const m = await measureSpot(step.spotId)
      if (!m) {
        console.warn(`Tour: could not measure spot "${step.spotId}", skipping`)
        const nextIdx = index + direction
        if (nextIdx >= 0 && nextIdx < config.steps.length) {
          return animateToStep(nextIdx, animate, direction)
        }
        return
      }

      // Bail if tour was stopped while we were awaiting
      if (!tourConfigRef.current) return

      // Convert absolute screen coords → overlay-relative coords
      const ox = overlayOffsetRef.current.x
      const oy = overlayOffsetRef.current.y
      const padding = step.padding ?? 4
      const x = m.x - ox - padding
      const y = m.y - oy - padding
      const w = m.width + padding * 2
      const h = m.height + padding * 2
      const r = step.borderRadius ?? 8

      // Update React state + store cutout rect for tooltip repositioning
      stepIndexRef.current = index
      setStepIndex(index)
      cutoutRectRef.current = { x, y, w, h }

      if (animate) {
        // 1. Fade out tooltip
        tooltipOpacity.value = withTiming(0, { duration: 150 })
        await delay(150)

        // 2. Swap content while invisible + slide cutout
        setCurrentStep(step)
        cutoutX.value = withTiming(x, MOVE_CONFIG)
        cutoutY.value = withTiming(y, MOVE_CONFIG)
        cutoutW.value = withTiming(w, MOVE_CONFIG)
        cutoutH.value = withTiming(h, MOVE_CONFIG)
        cutoutR.value = withTiming(r, MOVE_CONFIG)

        // 3. Wait for cutout to arrive — this also gives React plenty of
        //    time to render the new text and fire onLayout with the real
        //    tooltip height before we position it.
        await delay(MOVE_DURATION)

        // 4. NOW position with the correct measured height, then fade in
        positionTooltip()
        tooltipOpacity.value = withTiming(1, { duration: 200 })
      } else {
        // Directly set (used for the first step — no animation yet)
        setCurrentStep(step)
        cutoutX.value = x
        cutoutY.value = y
        cutoutW.value = w
        cutoutH.value = h
        cutoutR.value = r

        // Wait one frame for React to render the tooltip and fire onLayout
        // so tooltipHeight is accurate before we position. The overlay
        // hasn't faded in yet, so the user sees nothing during this wait.
        await delay(50)
        positionTooltip()
        tooltipOpacity.value = 1
      }
    },
    [
      screenHeight,
      waitForSpot,
      scrollSpotIntoView,
      measureSpot,
      positionTooltip,
      cutoutX,
      cutoutY,
      cutoutW,
      cutoutH,
      cutoutR,
      tooltipOpacity,
    ]
  )

  // ----- Public API --------------------------------------------------------

  const start = useCallback(
    async (config: TourConfig) => {
      if (config.steps.length === 0) return

      tourConfigRef.current = config
      setTotalSteps(config.steps.length)

      // Mount the overlay invisibly so we can measure its screen offset
      // (needed on Android where the overlay starts below the status bar)
      overlayOpacity.value = 0
      setIsActive(true)
      await delay(50) // one frame for the overlay to mount
      await measureOverlayOffset()

      // Measure + position the first step
      await animateToStep(0, false)

      // Fade in
      overlayOpacity.value = withTiming(1, { duration: 300 })
    },
    [animateToStep, overlayOpacity, measureOverlayOffset]
  )

  const stop = useCallback(() => {
    const config = tourConfigRef.current
    if (!config) return

    const completed = stepIndexRef.current === config.steps.length - 1
    tourConfigRef.current = null

    // Notify consumer immediately
    config.onStop?.(completed)

    // Fade out, then unmount
    overlayOpacity.value = withTiming(0, { duration: 200 }, (finished) => {
      if (finished) {
        runOnJS(setIsActive)(false)
      }
    })
  }, [overlayOpacity])

  const next = useCallback(async () => {
    if (isAnimatingRef.current) return
    const config = tourConfigRef.current
    if (!config) return

    const nextIndex = stepIndexRef.current + 1
    if (nextIndex >= config.steps.length) {
      stop()
      return
    }

    isAnimatingRef.current = true
    try {
      await animateToStep(nextIndex, true, 1)
    } finally {
      isAnimatingRef.current = false
    }
  }, [animateToStep, stop])

  const prev = useCallback(async () => {
    if (isAnimatingRef.current) return
    const prevIndex = stepIndexRef.current - 1
    if (prevIndex < 0) return

    isAnimatingRef.current = true
    try {
      await animateToStep(prevIndex, true, -1)
    } finally {
      isAnimatingRef.current = false
    }
  }, [animateToStep])

  // ----- Animated props / styles -------------------------------------------

  const animatedPathProps = useAnimatedProps(() => {
    return {
      d: buildOverlayPath(
        screenWidth,
        screenHeight,
        Math.max(0, cutoutX.value),
        Math.max(0, cutoutY.value),
        Math.max(0, cutoutW.value),
        Math.max(0, cutoutH.value),
        cutoutR.value
      ),
    }
  })

  const overlayAnimatedStyle = useAnimatedStyle(() => ({
    opacity: overlayOpacity.value,
  }))

  const tooltipAnimatedStyle = useAnimatedStyle(() => ({
    left: tooltipLeft.value,
    maxWidth: Math.min(screenWidth - TOOLTIP_H_MARGIN * 2, TOOLTIP_MAX_WIDTH),
    opacity: tooltipOpacity.value,
    position: 'absolute' as const,
    top: tooltipTop.value,
  }))

  // ----- Context value -----------------------------------------------------

  const contextValue = useMemo(
    () => ({
      isActive,
      registerSpot,
      start,
      stop,
      unregisterSpot,
    }),
    [registerSpot, unregisterSpot, start, stop, isActive]
  )

  // ----- Render ------------------------------------------------------------

  return (
    <TourContext.Provider value={contextValue}>
      {children}

      {isActive && (
        <Animated.View
          collapsable={false}
          ref={overlayRef}
          style={[
            StyleSheet.absoluteFill,
            overlayAnimatedStyle,
            styles.overlay,
          ]}
        >
          {/* Touch blocker — swallows all taps on the backdrop */}
          <View
            onStartShouldSetResponder={() => true}
            style={StyleSheet.absoluteFill}
          >
            <Svg
              height={screenHeight}
              pointerEvents="none"
              style={StyleSheet.absoluteFill}
              width={screenWidth}
            >
              <AnimatedPath
                animatedProps={animatedPathProps}
                fill={OVERLAY_COLOR}
                fillRule="evenodd"
              />
            </Svg>
          </View>

          {/* Tooltip */}
          {currentStep && (
            <Animated.View
              onLayout={(e) => {
                tooltipHeight.value = e.nativeEvent.layout.height
              }}
              style={tooltipAnimatedStyle}
            >
              <TourTooltip
                onNext={next}
                onPrev={prev}
                onStop={stop}
                spotId={currentStep.spotId}
                stepIndex={stepIndex}
                text={currentStep.text}
                totalSteps={totalSteps}
              />
            </Animated.View>
          )}
        </Animated.View>
      )}
    </TourContext.Provider>
  )
}

const styles = StyleSheet.create(() => ({
  overlay: {
    zIndex: 9999,
  },
}))
